import * as userService from '@/services/user.service';

import { queryKeys } from '@/query/query-keys';
import {
  notifyMutationErrorWithOptions,
  notifyMutationSuccessWithOptions,
} from '@/query/query-notifications';

import type { InviteUserFormValues, UpdateUserProfileFormValues } from '@/schemas/user.schema';

import type { PaginatedResponse, PaginationParams, QueryOptions } from '@/types/common.type';
import type { CurrentUser, User } from '@/types/user.type';
import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';

export const useUsersQuery = (
  params: PaginationParams,
  options?: Omit<UseQueryOptions<PaginatedResponse<User>, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: queryKeys.users.list(params.page || 1, params.page_size, params.query || ''),
    queryFn: () => userService.fetchAllUsers(params),
    ...options,
  });
};

export const useDeleteUserMutation = (options?: QueryOptions<void>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.users.mutations.delete,
    mutationFn: userService.deleteUserById,
    onSuccess: () => {
      notifyMutationSuccessWithOptions(options, 'User removed', 'User was removed successfully.');
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      options?.onSuccess?.();
    },
    onError: error => {
      notifyMutationErrorWithOptions(options, 'Removing user failed', error);
      options?.onError?.(error);
    },
  });
};

export const useUpdateUserRoleMutation = (options?: QueryOptions<void>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.users.mutations.updateRole,
    mutationFn: ({ userId, role }: { userId: number; role: string }) =>
      userService.updateUserRole(userId, role),
    onSuccess: () => {
      notifyMutationSuccessWithOptions(
        options,
        'User role updated',
        'User role was updated successfully.'
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      options?.onSuccess?.();
    },
    onError: error => {
      notifyMutationErrorWithOptions(options, 'Updating user role failed', error);
      options?.onError?.(error);
    },
  });
};

export const useFetchCurrentUser = (
  options?: Omit<UseQueryOptions<CurrentUser, Error>, 'queryKey' | 'queryFn' | 'placeholderData'>
) => {
  return useQuery({
    queryKey: queryKeys.users.current,
    queryFn: () => userService.getCurrentUser(),
    staleTime: Infinity,
    placeholderData: {
      id: 0,
      first_name: '',
      last_name: '',
      full_name: '',
      email: '',
      cognito_id: '',
      created_at: new Date().toISOString(),
      last_logged_in_at: new Date().toISOString(),
      role: { id: 0, name: '' },
      permissions: [],
    },
    ...options,
  });
};

export const useUpdateUserProfileMutation = (options?: QueryOptions<CurrentUser>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.users.mutations.updateProfile,
    mutationFn: (data: UpdateUserProfileFormValues) => userService.updateUserProfile(data),
    onMutate: async newData => {
      await queryClient.cancelQueries({ queryKey: queryKeys.users.current });

      const previousUser = queryClient.getQueryData<CurrentUser>(queryKeys.users.current);

      if (previousUser) {
        queryClient.setQueryData<CurrentUser>(queryKeys.users.current, {
          ...previousUser,
          ...newData,
          full_name: `${newData.first_name || previousUser.first_name} ${newData.last_name || previousUser.last_name}`,
        });
      }

      return { previousUser };
    },
    onSuccess: data => {
      notifyMutationSuccessWithOptions(
        options,
        'Profile updated',
        'Your profile has been updated.'
      );
      queryClient.setQueryData(queryKeys.users.current, data);
      options?.onSuccess?.(data);
    },
    onError: (error, _variables, context) => {
      if (context?.previousUser) {
        queryClient.setQueryData(queryKeys.users.current, context.previousUser);
      }
      notifyMutationErrorWithOptions(options, 'Profile update failed', error);
      options?.onError?.(error);
    },
  });
};

export const useInviteUserMutation = (options?: QueryOptions<string>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.users.mutations.invite,
    mutationFn: (data: InviteUserFormValues) => userService.inviteUser(data),
    onSuccess: message => {
      notifyMutationSuccessWithOptions(options, 'Invitation sent', message);
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      options?.onSuccess?.(message);
    },
    onError: error => {
      notifyMutationErrorWithOptions(options, 'Sending invitation failed', error);
      options?.onError?.(error);
    },
  });
};

export const useResendInvitationMutation = (options?: QueryOptions<void>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.users.mutations.resendInvitation,
    mutationFn: (userId: number) => userService.resendInvitation(userId),
    onSuccess: () => {
      notifyMutationSuccessWithOptions(
        options,
        'Invitation resent',
        'Invitation email was resent successfully.'
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.invitations });
      options?.onSuccess?.();
    },
    onError: error => {
      notifyMutationErrorWithOptions(options, 'Resending invitation failed', error);
      options?.onError?.(error);
    },
  });
};
